import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";

export interface GraphMetaSummary {
  node_count: number;
  edge_count: number;
  node_types: Record<string, number>;
}

export interface PatternsSummary {
  total: number;
  by_state: Record<string, number>;
  avg_effective_score: number;
}

export interface TokenMetaSummary {
  token_event_count: number;
  session_count: number;
  total_output_tokens: number;
  total_cost_usd: number;
}

export interface WorkMetaSummary {
  active_epic: string | null;
  active_story: string | null;
  event_count: number;
  stories_closed: number;
  pipeline_runs: number;
}

export interface GateMetaSummary {
  total: number;
  passing: number;
  failing: number;
}

export interface MemoryMeta {
  project_id: string;
  generated_at: string;
  graph: GraphMetaSummary;
  patterns: PatternsSummary;
  tokens: TokenMetaSummary;
  work: WorkMetaSummary;
  gates: GateMetaSummary;
}

export function useMemoryMeta(projectSlug: string | null) {
  // non-blocking — meta is decorative (pre-existing behavior, preserved).
  const query = useQuery({
    queryKey: queryKeys.memory.meta(projectSlug ?? ""),
    queryFn: async () => {
      try {
        return await api<MemoryMeta>(
          `/api/v2/projects/${projectSlug}/memory/meta`,
        );
      } catch {
        return null;
      }
    },
    enabled: projectSlug !== null,
  });

  return { meta: query.data ?? null, loading: query.isLoading };
}
